import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { MOBILE_QUERY } from "@/hooks/use-mobile";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    overlayZIndex?: number;
  }
>(({ className, overlayZIndex = 100, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    // Inline, as any value can be passed: Tailwind only generates z-[…] classes it finds spelled out in the source.
    style={{ zIndex: overlayZIndex, ...style }}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const sizeClasses = {
  sm: "max-w-[480px]",
  md: "max-w-[560px]",
  template: "max-w-[720px]",
  lg: "max-w-[1024px]",
  xl: "max-w-[1280px]",
  "2xl": "max-w-[1536px]"
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    includeCloseButton?: boolean;
    size?: "sm" | "md" | "template" | "lg" | "xl" | "2xl";
    hideBackdrop?: boolean;
    preventOverlayClick?: boolean;
    overlayZIndex?: number;
  }
>(
  (
    {
      className,
      children,
      includeCloseButton = true,
      size = "lg",
      overlayZIndex = 100,
      hideBackdrop = false,
      preventOverlayClick = false,
      onOpenAutoFocus,
      ...props
    },
    ref
  ) => {
    // The content is fixed and vertically centred, so without a cap tall dialogs
    // grow past both edges of the viewport with no way to reach the clipped parts.
    // A height class is the signal that a dialog has thought about its own vertical
    // space - usually a `flex flex-col` shell with an inner scroll area - so those
    // keep the cap and the scrolling they set for themselves. Overflow alone is not
    // that signal: `overflow-hidden` on a full-bleed dialog is normally there to
    // clip rounded corners, and skipping the scroll for it just moves where the
    // unreachable content gets cut off.
    const managesOwnHeight = /\b(max-)?h-/.test(className ?? "");

    return (
      <DialogPortal>
        {!hideBackdrop && <DialogOverlay overlayZIndex={overlayZIndex} />}
        <DialogPrimitive.Content
          ref={ref}
          onInteractOutside={(e) => {
            if (preventOverlayClick) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (preventOverlayClick) {
              e.preventDefault();
            }
          }}
          // Below 768px the dialog focuses itself rather than its first field, so opening one doesn't raise the
          // phone's keyboard before anything is tapped; screen readers still move into it. A consumer's own handler
          // runs first, and one that takes over (preventDefault) is left alone.
          onOpenAutoFocus={(e) => {
            onOpenAutoFocus?.(e);
            const isPhone = typeof window.matchMedia === "function" && window.matchMedia(MOBILE_QUERY).matches;
            if (e.defaultPrevented || !isPhone) return;
            e.preventDefault();
            (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
          }}
          className={cn(
            "fixed left-[50%] top-[50%] z-[100] grid w-[95%] translate-x-[-50%] translate-y-[-50%] gap-4 border border-[#444444] bg-background p-[var(--dialog-padding)] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg",
            // 32px of padding, 24px below 768px. Set through a variable so a dialog's own padding class (e.g. `p-0`)
            // replaces it at every width.
            "[--dialog-padding:24px] md:[--dialog-padding:32px]",
            // 95% wide with rounded corners at every width, so a dialog never runs edge to edge on a phone.
            // `sizeClasses` caps it on wider screens; a consumer's own width or radius still wins.
            sizeClasses[size],
            className,
            // Applied after className so a consumer's own `overflow-hidden` cannot
            // strip the scrolling back off and leave the capped dialog unreachable
            !managesOwnHeight && "max-h-[90vh] overflow-y-auto"
          )}
          {...props}
        >
          {children}
          {includeCloseButton && (
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

/**
 * The footer's own buttons and links: sharing the row evenly, up to 200px each from 640px, and never narrower than
 * their label, so a row whose labels don't fit wraps instead. They grow from nothing rather than shrinking from 200px,
 * because a wrapping row breaks lines on that starting width: from 200px, two buttons in a 480px dialog would wrap
 * instead of sharing it. Elements the footer wraps keep their own widths. Inside `:where()`, so the rules carry no
 * specificity and a button's own width classes win. Written out in full, as Tailwind only generates classes it finds
 * spelled out in the source.
 */
const STANDARD_FOOTER_BUTTON_CLASSES =
  "[:where(&>button)]:min-w-fit [:where(&>button)]:flex-1 sm:[:where(&>button)]:max-w-[200px] [:where(&>a)]:min-w-fit [:where(&>a)]:flex-1 sm:[:where(&>a)]:max-w-[200px]";

/**
 * How a footer arranges what's in it.
 * - `row` (default): side by side at every width, at the end, 16px apart, wrapping where they don't fit.
 * - `spread`: one row with its first element at the start and the rest at the end (e.g. Clear All, then Cancel and
 *   Apply).
 * - `stacked-spread`: a column on a phone, its first element on top; from 640px, `spread`.
 * - `stacked-spread-reverse`: a column on a phone with its last element on top (the primary action, above a note);
 *   from 640px, `spread`.
 * - `wrap-start`: side by side and wrapping, at the start on a phone and at the end from 640px.
 * - `split`: one row its buttons share evenly, at every width (e.g. Cancel and Delete filling the dialog's width).
 */
const FOOTER_LAYOUT_CLASSES = {
  row: "flex-row flex-wrap justify-end gap-4",
  spread: "flex-row flex-nowrap justify-between gap-4",
  "stacked-spread":
    "flex-col flex-nowrap items-stretch justify-start gap-2 sm:flex-row sm:items-center sm:justify-between",
  "stacked-spread-reverse": "flex-col-reverse flex-nowrap justify-start gap-2 sm:flex-row sm:justify-between",
  "wrap-start": "flex-row flex-wrap items-center justify-start gap-2 sm:justify-end",
  split: "flex-row flex-nowrap gap-4 [:where(&>button)]:min-w-0 [:where(&>button)]:flex-1"
} as const;

const DialogFooter = ({
  className,
  layout = "row",
  buttonWidth = layout === "row" ? "standard" : "natural",
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  layout?: keyof typeof FOOTER_LAYOUT_CLASSES;
  /**
   * "standard" gives the footer's own buttons `STANDARD_FOOTER_BUTTON_CLASSES`; "natural" sizes them to their labels.
   * Defaults to "standard" for `row` and "natural" for the other layouts, whose footers hold more than a button pair.
   */
  buttonWidth?: "standard" | "natural";
}) => (
  <div
    className={cn(
      "flex",
      FOOTER_LAYOUT_CLASSES[layout],
      buttonWidth === "standard" && STANDARD_FOOTER_BUTTON_CLASSES,
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("pr-6 text-5xl font-instrument-serif font-medium leading-[56px] line-clamp-2", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-secondary", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
};
